import { useState } from 'react';
import { Button, DatePicker, Descriptions, Form, Input, Modal, Select, Space, Table, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useFinanceTable from '../../../hooks/useFinanceTable';
import { vatExport, vatList, vatOrders, vatStatusUpdate } from '../../../api/finance';
import FinanceExportButton from '../../../components/finance/FinanceExportButton';
import FinanceDetailModal from '../../../components/finance/FinanceDetailModal';
import FinanceStatusTag from '../../../components/finance/FinanceStatusTag';
import { can } from '../../../util/permission';
import { formatDateTime, formatMoney } from '../../../util/finance';

export default function VatTab() {
  const table = useFinanceTable(vatList);
  const [form, setForm] = useState({ taxMonth: null, realName: '', userId: '', overLimit: undefined });
  const [orders, setOrders] = useState([]);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [ordersTitle, setOrdersTitle] = useState('');
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusRecord, setStatusRecord] = useState(null);
  const [statusForm] = Form.useForm();

  const search = () => {
    table.search({
      taxMonth: form.taxMonth ? dayjs(form.taxMonth).format('YYYY-MM') : undefined,
      realName: form.realName || undefined,
      userId: form.userId || undefined,
      overLimit: form.overLimit,
    });
  };

  const reset = () => {
    setForm({ taxMonth: null, realName: '', userId: '', overLimit: undefined });
    table.reset();
  };

  const openOrders = async (record) => {
    try {
      const res = await vatOrders({ userId: record.userId, taxMonth: record.taxMonth });
      setOrders(res || []);
      setOrdersTitle(`${record.realName || record.userId} · ${record.taxMonth} 订单收入`);
      setOrdersOpen(true);
    } catch (error) {
      message.error(error?.message || '查询订单明细失败');
    }
  };

  const openStatus = (record) => {
    setStatusRecord(record);
    statusForm.setFieldsValue({
      declareStatus: record.declareStatus ?? 0,
      declarationNo: record.declarationNo,
      taxVoucherNo: record.taxVoucherNo,
      remark: record.remark,
    });
    setStatusOpen(true);
  };

  const submitStatus = async () => {
    try {
      const values = await statusForm.validateFields();
      await vatStatusUpdate({
        userId: statusRecord.userId,
        taxMonth: statusRecord.taxMonth,
        ...values,
      });
      message.success('申报状态已更新');
      setStatusOpen(false);
      table.reload();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error?.message || '更新失败');
    }
  };

  const columns = [
    { title: '所属月份', dataIndex: 'taxMonth', width: 100, fixed: 'left' },
    { title: '用户ID', dataIndex: 'userId', width: 100 },
    { title: '姓名', dataIndex: 'realName', width: 100 },
    { title: '身份证号', dataIndex: 'idCardNo', width: 180 },
    { title: '手机号', dataIndex: 'phone', width: 130 },
    { title: '当月平台总收入', dataIndex: 'platformIncome', width: 140, render: formatMoney },
    { title: '订单数', dataIndex: 'orderCount', width: 80 },
    {
      title: '是否超过10万',
      dataIndex: 'overLimitText',
      width: 120,
      render: (text) => <FinanceStatusTag text={text} color={text === '是' ? 'red' : 'default'} />,
    },
    {
      title: '申报状态',
      dataIndex: 'declareStatusText',
      width: 100,
      render: (text) => <FinanceStatusTag text={text} />,
    },
    { title: '申报表流水号', dataIndex: 'declarationNo', width: 160, ellipsis: true },
    { title: '完税凭证号', dataIndex: 'taxVoucherNo', width: 160, ellipsis: true },
    { title: '申报时间', dataIndex: 'declareAt', width: 170, render: formatDateTime },
    { title: '缴款时间', dataIndex: 'paidAt', width: 170, render: formatDateTime },
    {
      title: '操作',
      key: 'action',
      width: 170,
      fixed: 'right',
      render: (_, record) => (
        <Space size={0}>
          <Button type="link" onClick={() => openOrders(record)}>
            订单明细
          </Button>
          {can('finance:vat:edit') && (
            <Button type="link" onClick={() => openStatus(record)}>
              维护状态
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="finance-toolbar">
        <DatePicker
          picker="month"
          value={form.taxMonth}
          onChange={(value) => setForm({ ...form, taxMonth: value })}
          placeholder="所属月份"
          style={{ width: 140 }}
        />
        <Input
          value={form.realName}
          onChange={(e) => setForm({ ...form, realName: e.target.value })}
          placeholder="姓名"
          style={{ width: 140 }}
          allowClear
        />
        <Input
          value={form.userId}
          onChange={(e) => setForm({ ...form, userId: e.target.value.replace(/\D/g, '') })}
          placeholder="用户ID"
          style={{ width: 120 }}
          allowClear
        />
        <Select
          value={form.overLimit}
          onChange={(value) => setForm({ ...form, overLimit: value })}
          placeholder="是否超免征额"
          allowClear
          style={{ width: 150 }}
          options={[
            { value: 1, label: '超过10万' },
            { value: 0, label: '未超过10万' },
          ]}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={search}>
          查询
        </Button>
        <Button onClick={reset}>重置</Button>
        {can('finance:vat:export') && (
          <FinanceExportButton exportApi={vatExport} query={table.query} />
        )}
      </div>
      <Table
        className="finance-table"
        columns={columns}
        dataSource={table.data}
        rowKey={(record) => `${record.userId}-${record.taxMonth}`}
        loading={table.loading}
        scroll={{ x: 1800 }}
        rowClassName={(_, index) => (index % 2 === 1 ? 'finance-row-odd' : '')}
        pagination={{
          current: table.pagination.current,
          pageSize: table.pagination.pageSize,
          total: table.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条数据`,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: table.onTableChange,
        }}
      />
      <FinanceDetailModal open={ordersOpen} title={ordersTitle} onClose={() => setOrdersOpen(false)}>
        <Table
          size="small"
          rowKey="taskId"
          dataSource={orders}
          pagination={false}
          columns={[
            { title: '订单号', dataIndex: 'taskId', width: 100 },
            { title: '任务名称', dataIndex: 'taskTitle', ellipsis: true },
            { title: '税前赏金', dataIndex: 'grossAmount', width: 120, render: formatMoney },
            { title: '本笔扣税', dataIndex: 'currentTax', width: 110, render: formatMoney },
            { title: '税后净额', dataIndex: 'netAmount', width: 120, render: formatMoney },
            { title: '计税时间', dataIndex: 'calculatedAt', width: 170, render: formatDateTime },
          ]}
        />
      </FinanceDetailModal>
      <Modal
        open={statusOpen}
        title="维护增值税申报状态"
        onCancel={() => setStatusOpen(false)}
        onOk={submitStatus}
        okText="保存"
      >
        <Form form={statusForm} layout="vertical">
          <Form.Item label="申报状态" name="declareStatus" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 0, label: '未申报' },
                { value: 1, label: '已申报' },
                { value: 2, label: '已缴款' },
              ]}
            />
          </Form.Item>
          <Form.Item label="申报表流水号" name="declarationNo">
            <Input placeholder="申报表流水号" />
          </Form.Item>
          <Form.Item label="完税凭证号" name="taxVoucherNo">
            <Input placeholder="完税凭证号" />
          </Form.Item>
          <Form.Item label="备注" name="remark">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
        <Descriptions size="small" column={2}>
          <Descriptions.Item label="用户">{statusRecord?.realName || statusRecord?.userId}</Descriptions.Item>
          <Descriptions.Item label="月份">{statusRecord?.taxMonth}</Descriptions.Item>
          <Descriptions.Item label="当月收入" span={2}>
            {formatMoney(statusRecord?.platformIncome)}
          </Descriptions.Item>
        </Descriptions>
      </Modal>
    </div>
  );
}
